import '../text-and-lang/as-text.ts';
import '../parameters/as-param-group.ts';
import '../layout/as-accordion.ts';
import '../inputs/as-checkbox.ts';
import '../inputs/as-string-input.ts';
import '../inputs/as-dropdown.ts';
import '../base/buttons/as-button.ts';
import '../base/buttons/as-icon-button.ts';
import '../base/as-op-status.ts';
import '../overlays/as-hovercard-wrapper.ts';
import '../overlays/as-hovercard.ts';
import type { AsOpStatus } from '../base/as-op-status.ts';
import { wireDownloadButton, DOWNLOAD_DISABLED_TOOLTIP, type DownloadWiring } from './download-wiring.ts';
import styleText from './as-export-step.css?raw';
import { ExportBuilder } from '../../logic/importExport/exportBuilder.ts';
import { liveAppData } from '../../store/store.ts';
import { DeprecatedEl } from '../../logic/deprecatedEl.ts';
import type { FileDataSchema, MeshType } from '../../data/data-interfaces.external.ts';

/**
 * <as-export-step>
 *
 * Drop-in export step: renders two <as-param-group> cards in light DOM so they
 * participate directly in the parent <as-step-panel>'s flex layout.
 *
 * Panels
 *   1. Export           — Save file / Transfer to GrabCAD / Send to other apps
 *   2. Export scope     — scope checkboxes (default: outputPart, workpiece, supports)
 *
 * The design preview card is handled by <as-export-preview-panel>, which should be
 * placed as a sibling before this element inside the parent <as-step-panel>.
 *
 * Builder API (call after element is connected)
 *   step.packageName = 'my-part'          — File name input + download + GrabCAD package-name
 *   step.saveFormats = [...]              — override default STL/3MF options
 *   step.exporter = builder               — explicit exporter; defaults to as-app.exporter
 *   step.addExportScope(item)             — add a scope checkbox; chainable
 *   step.addChildScope(parentKey, item)   — add an indented child under an existing scope; chainable
 *   step.removeExportScope(key)           — remove a scope checkbox by key; chainable
 *   step.hasKey(key)                      — returns true if the scope key is registered
 *   step.scopes[key]                      — live accessor: .checked, .visible, .fileCfgs, .label
 *   step.exportScopes[key]                — deprecated alias for step.scopes[key]
 *   step.setFromObject(config)            — bulk set (renamed from configure)
 *   step.reset()                          — reset all button statuses
 *   step.downloadButton / step.downloadStatus / step.transferButton
 *                                         — the Save file <as-button>, its <as-op-status>,
 *                                           and the "Send to other apps…" <as-button>
 *
 * Attributes
 *   no-default-scopes    — suppress the built-in outputPart / workpiece / supports checkboxes
 *   no-default-exporter  — disable auto-fallback to as-app.exporter
 *   no-transfer-button   — hide the "Send to other apps…" button
 *
 * Scope checkboxes bubble as-input-change with name = item key.
 */

export interface ExportScopeConfig {
  key: string;
  defaultLabelText: string;
  labelDictKey?: string;
  checked?: boolean;
  visible?: boolean;
  fileCfgs?: FileDataSchema[];
  children?: ExportScopeConfig[];
}

export interface FormatOption {
  value: string;
  label: string;
}

export interface ExportStepConfig {
  packageName?: string;
  scopeItems?: ExportScopeConfig[];
  saveFormats?: FormatOption[];
  exporter?: ExportBuilder;
}

/** Public type for a scope entry returned by scopes. */
export interface ExportScope {
  readonly key: string;
  defaultLabelText: string;
  checked: boolean;
  readonly disabled: boolean;
  visible: boolean;
  fileCfgs: FileDataSchema[];
  readonly el: HTMLElement | undefined;
}

class ExportScopeConfigAccessor implements ExportScope {
  readonly key: string;
  #defaultLabelText: string;
  fileCfgs: FileDataSchema[];
  readonly #elRef: WeakRef<HTMLElement>;
  #childKeys: string[] = [];
  #visible: boolean;
  #onVisibilityChange: (() => void) | undefined;

  constructor(key: string, defaultLabelText: string, el: HTMLElement, fileCfgs?: FileDataSchema[], visible = true) {
    this.key = key;
    this.#defaultLabelText = defaultLabelText;
    this.fileCfgs = fileCfgs ?? [];
    this.#elRef = new WeakRef(el);
    this.#visible = visible;
    if (!visible) el.style.display = 'none';
  }

  get defaultLabelText(): string { return this.#defaultLabelText; }
  set defaultLabelText(v: string) {
    this.#defaultLabelText = v;
    this.#elRef.deref()?.setAttribute('label-default-text', v);
  }

  get checked(): boolean { return (this.#elRef.deref() as any)?.checked ?? false; }
  set checked(v: boolean) { const el = this.#elRef.deref(); if (el) (el as any).checked = v; }

  get disabled(): boolean { return this.#elRef.deref()?.hasAttribute('disabled') ?? false; }

  get visible(): boolean { return this.#visible; }
  set visible(v: boolean) {
    this.#visible = v;
    const el = this.#elRef.deref();
    if (el) el.style.display = v ? '' : 'none';
    this.#onVisibilityChange?.();
  }

  get el(): HTMLElement | undefined { return this.#elRef.deref(); }

  _setOnVisibilityChange(cb: () => void): void { this.#onVisibilityChange = cb; }
  _setChildKeys(keys: string[]): void { this.#childKeys = [...keys]; }
  _addChildKey(key: string): void { this.#childKeys.push(key); }
  get _childKeys(): readonly string[] { return this.#childKeys; }
}

const SAVE_TOOLTIP = 'Save the selected models to your computer.';

const DEFAULT_FORMATS: FormatOption[] = [
  { value: 'stl', label: 'STL' },
  { value: '3mf', label: '3MF' },
];

const DEFAULT_SCOPES: ExportScopeConfig[] = [
  { key: 'trcadMesh', defaultLabelText: 'Output parts' },
  { key: 'workpiece', defaultLabelText: 'Workpieces' },
  { key: 'standard-components', defaultLabelText: 'Standard Components' },
];

export class AsExportStep extends HTMLElement {
  #config: ExportStepConfig = {};
  #exporter: ExportBuilder | null = null;
  #observer: MutationObserver | null = null;
  #built = false;

  #scopeGroupEl: HTMLElement | null = null;
  #exportSettingsButton: HTMLElement | null = null;
  #filenameInputEl: HTMLElement | null = null;
  #formatDropdownEl: HTMLElement | null = null;
  #downloadWiring: DownloadWiring | null = null;
  /** The "Save file" <as-button>. */
  downloadButton: HTMLElement | null = null;
  /** The <as-op-status> showing download progress/result. */
  downloadStatus: HTMLElement | null = null;
  /** The "Send to other apps…" <as-button>. */
  transferButton: HTMLElement | null = null;
  #gcBtnEntries: Array<{ el: HTMLElement; requiredEnv?: string; extraAttrs: string[] }> = [];
  #scopeMap: Map<string, ExportScopeConfigAccessor> = new Map();
  #nameUserEdited = false;
  #activeProjectId: string | null = null;
  #updatingName = false;
  #removeProjectEffect: (() => void) | null = null;
  #removeAppIdEffect: (() => void) | null = null;
  #removeCanDownloadEffect: (() => void) | null = null;
  #onResetBound = (): void => this._handleSessionReset();

  private static _styleInjected = false;

  static readonly observedAttributes = ['no-transfer-button'];

  constructor() {
    super();
  }

  attributeChangedCallback(name: string, _old: string | null, next: string | null): void {
    if (name === 'no-transfer-button' && this.transferButton) {
      this.transferButton.style.display = next !== null ? 'none' : '';
    }
  }

  connectedCallback(): void {
    if (!AsExportStep._styleInjected) {
      const style = document.createElement('style');
      style.textContent = styleText;
      style.setAttribute('data-component', 'as-export-step');
      document.head.appendChild(style);
      AsExportStep._styleInjected = true;
    }
    if (!this.#built) this._build();
    this.#downloadWiring ??= wireDownloadButton({
      button: this.downloadButton!,
      status: () => this.downloadStatus as AsOpStatus | null,
      getExporter: () => this.exporter,
      getFilename: () => this.packageName ? `${this.packageName}.zip` : undefined,
      getExporterId: () => this.exporter?.exporterId ?? 'default',
    });
    this.#removeProjectEffect = liveAppData.addSideEffect('currentProject', () => this._syncDefaultName());
    // appId can be set after this element connects, so re-sync the default name
    // once it arrives — otherwise the untitled default lacks the app suffix.
    this.#removeAppIdEffect = liveAppData.addSideEffect('appId', () => this._syncDefaultName());
    this.#removeCanDownloadEffect = liveAppData.addSideEffect('canDownload', () => this._syncDownloadEnabled());
    this._syncDefaultName();
    this._syncDownloadEnabled();
    // Reset on a new session. `as-inputs-reset` is the canonical broadcast fired
    // by managed project handling (createAndSetNewProject); `as-session-manual-reset`
    // is the explicit hook for hosts running with disable-session-handlers, which
    // never trigger the managed path.
    window.addEventListener('as-inputs-reset', this.#onResetBound);
    window.addEventListener('as-session-manual-reset', this.#onResetBound);
    const app = document.querySelector('as-app');
    if (app) {
      this.#observer = new MutationObserver(() => this._updateEnv());
      this.#observer.observe(app, { attributes: true, attributeFilter: ['env'] });
    }
    this._updateEnv();
  }

  disconnectedCallback(): void {
    this.#downloadWiring?.dispose();
    this.#downloadWiring = null;
    this.#observer?.disconnect();
    this.#observer = null;
    this.#removeProjectEffect?.();
    this.#removeProjectEffect = null;
    this.#removeAppIdEffect?.();
    this.#removeAppIdEffect = null;
    this.#removeCanDownloadEffect?.();
    this.#removeCanDownloadEffect = null;
    window.removeEventListener('as-inputs-reset', this.#onResetBound);
    window.removeEventListener('as-session-manual-reset', this.#onResetBound);
  }

  /**
   * Restore the step to its just-loaded state on a new session: drop the
   * user-edited-name flag so the default filename re-derives, re-sync that name,
   * and clear any lingering download/transfer button statuses.
   */
  private _handleSessionReset(): void {
    this.#nameUserEdited = false;
    this._syncDefaultName();
    this.reset();
  }

  // ── Public API ────────────────────────────────────────────────────────────────

  /** Current format dropdown value; falls back to first saveFormats entry or 'stl'. */
  get format(): string {
    return (this.#formatDropdownEl as any)?.value
      ?? this.#config.saveFormats?.[0]?.value
      ?? DEFAULT_FORMATS[0].value;
  }

  /** Explicit ExportBuilder to use; falls back to as-app.exporter unless no-default-exporter is set. */
  get exporter(): ExportBuilder | null {
    if (this.#exporter) return this.#exporter;
    if (this.hasAttribute('no-default-exporter')) return null;
    return (document.querySelector('as-app') as any)?.exporter ?? null;
  }

  set exporter(v: ExportBuilder | null) {
    this.#exporter = v;
  }

  /** Live map of all registered scope entries, keyed by scope key. outputPart is a backwards-compat alias for trcadMesh. */
  get scopes(): Record<string, ExportScope> {
    const map = Object.fromEntries(
      [...this.#scopeMap.entries()].map(([k, acc]) => [k, this.#toExternalScope(acc)])
    );
    if ('trcadMesh' in map && !('outputPart' in map)) map['outputPart'] = map['trcadMesh'];
    return map;
  }

  /** @deprecated Use `scopes` instead. */
  get exportScopes(): Record<string, ExportScope> {
    console.warn('as-export-step: exportScopes is deprecated — use scopes');
    return this.scopes;
  }

  /** Returns true if a scope with the given key is registered. outputPart is a backwards-compat alias for trcadMesh. */
  hasKey(key: string): boolean {
    return this.#scopeMap.has(this.#resolveKey(key));
  }

  #resolveKey(key: string): string {
    return key === 'outputPart' ? 'trcadMesh' : key;
  }

  #toExternalScope(acc: ExportScopeConfigAccessor): ExportScope {
    return {
      get key()              { return acc.key; },
      get defaultLabelText() { return acc.defaultLabelText; },
      set defaultLabelText(v: string) { acc.defaultLabelText = v; },
      get checked()          { return acc.checked; },
      set checked(v: boolean) { acc.checked = v; },
      get disabled()         { return acc.disabled; },
      get visible()          { return acc.visible; },
      set visible(v: boolean) { acc.visible = v; },
      get fileCfgs()         { return acc.fileCfgs; },
      set fileCfgs(v: FileDataSchema[]) { acc.fileCfgs = v; },
      get el()               { return acc.el; },
    };
  }

  /** Get the current package name: reflects live user edits to the filename input, falling back to the configured value. */
  get packageName(): string | undefined {
    const live = (this.#filenameInputEl as any)?.value;
    return live != null && live !== '' ? live : this.#config.packageName;
  }

  /** Set the package name: updates the filename input, download filename, and GrabCAD package-name attribute. */
  set packageName(v: string) {
    this.#config.packageName = v;
    if (this.#filenameInputEl) this.#filenameInputEl.setAttribute('value', v);
    for (const { el, extraAttrs } of this.#gcBtnEntries) {
      if (extraAttrs.includes('package-name')) el.setAttribute('package-name', v);
    }
  }

  /** Override the format dropdown options (default: STL, 3MF). */
  set saveFormats(v: FormatOption[]) {
    this.#config.saveFormats = v;
    this._syncFormatOptions();
  }

  /** Add a top-level scope checkbox. Chainable. */
  addExportScope(item: ExportScopeConfig): this {
    if (this.#built && this.#scopeGroupEl) {
      this._addExportScopeConfig(item, this.#scopeGroupEl);
      this._updateSingleScopeState();
    } else {
      this.#config.scopeItems = [...(this.#config.scopeItems ?? []), item];
    }
    return this;
  }

  /**
   * Add an indented child scope under an existing scope. The child participates in the
   * parent's enable/disable behaviour and is folded into the parent's FileDataSchema.children
   * at export time. Chainable.
   */
  addChildScope(parentKey: string, child: ExportScopeConfig): this {
    const parentAccessor = this.#scopeMap.get(this.#resolveKey(parentKey));
    if (!parentAccessor?.el) return this;

    const childCb = document.createElement('as-checkbox');
    childCb.setAttribute('label-default-text', child.defaultLabelText);
    if (child.labelDictKey) childCb.setAttribute('label-dict-key', child.labelDictKey);
    childCb.setAttribute('name', child.key);
    childCb.classList.add('export-scope-child');
    if (child.checked !== false) childCb.setAttribute('checked', '');
    if (!parentAccessor.checked) childCb.setAttribute('disabled', '');

    const childAccessor = new ExportScopeConfigAccessor(child.key, child.defaultLabelText, childCb, child.fileCfgs);
    this.#scopeMap.set(child.key, childAccessor);

    const existingChildKeys = parentAccessor._childKeys;
    const insertAfter = existingChildKeys.length > 0
      ? this.#scopeMap.get(existingChildKeys[existingChildKeys.length - 1])?.el ?? parentAccessor.el
      : parentAccessor.el;
    insertAfter!.insertAdjacentElement('afterend', childCb);

    parentAccessor._addChildKey(child.key);
    childCb.addEventListener('as-input-change', () => this._rebuildExporter());
    return this;
  }

  /** Remove a scope checkbox and all its registered children by key. Chainable. */
  removeExportScope(key: string): this {
    const resolved = this.#resolveKey(key);
    const accessor = this.#scopeMap.get(resolved);
    if (!accessor) return this;
    for (const childKey of accessor._childKeys) {
      this.#scopeMap.get(childKey)?.el?.remove();
      this.#scopeMap.delete(childKey);
    }
    accessor.el?.remove();
    this.#scopeMap.delete(resolved);
    this._updateSingleScopeState();
    return this;
  }

  /** Bulk-set configuration; builds the DOM if not yet connected, otherwise applies incrementally. */
  setFromObject(config: ExportStepConfig): void {
    if (config.exporter !== undefined) this.#exporter = config.exporter;
    Object.assign(this.#config, config);
    if (!this.isConnected) return;
    if (!this.#built) {
      this._build();
    } else {
      this._applyConfig();
    }
  }

  reset(): void {
    this.#downloadWiring?.reset();
    this.#gcBtnEntries.forEach(({ el }) => (el as any)?.reset?.());
  }

  // ── Private ───────────────────────────────────────────────────────────────────

  /** Parse an HTML string into a DocumentFragment via a detached <template> element. */
  private static _html(str: string): DocumentFragment {
    const tpl = document.createElement('template');
    tpl.innerHTML = str;
    return tpl.content;
  }

  /** If exactly one top-level scope is visible, force it checked + disabled; otherwise restore all to enabled. */
  private _updateSingleScopeState(): void {
    const childKeySet = new Set<string>();
    for (const [, acc] of this.#scopeMap) {
      for (const key of acc._childKeys) childKeySet.add(key);
    }
    const visible = [...this.#scopeMap.values()].filter(a => !childKeySet.has(a.key) && a.visible);
    const solo = visible.length === 1;
    for (const a of visible) {
      if (solo) {
        a.checked = true;
        a.el?.setAttribute('disabled', '');
      } else {
        a.el?.removeAttribute('disabled');
      }
    }
  }

  /** First-time DOM construction: creates both panels and appends them in order. */
  private _build(): void {
    this.#built = true;
    this.appendChild(this._buildActionsPanel());
    this.appendChild(this._buildScopePanel());
    this._updateSingleScopeState();
    this._updateEnv();
  }

  /** Build the Export panel: download button, settings cog hovercard, GrabCAD buttons, and transfer button. */
  private _buildActionsPanel(): HTMLElement {
    const frag = AsExportStep._html(/*html*/`
      <as-param-group subheading-default-text="Export">
        <div class="export-actions-row">
          <div class="export-save-row">
            <as-button
              name="export-all"
              default-text="Save file"
              no-default-icon
              tooltip-default-text="${SAVE_TOOLTIP}"
              tooltip-placement="right">
            </as-button>
            <as-op-status class="download-status"></as-op-status>
            <as-hovercard-wrapper id="export-settings-btn" class="export-settings-toggle"
              toggle-on-click show-on-hover="false" placement="right-bottom" offset="12">
              <as-icon-button class="export-settings-cog"
                size="s" icon-set="settings"
                black toggle name="toggle-export-file-settings"
                tooltip-default-text="Save file settings" tooltip-placement="right">
              </as-icon-button>
              <as-hovercard class="export-settings-hovercard">
                <div class="export-settings-content">
                  <p class="export-settings-heading">Save file settings</p>
                  <as-string-input
                    label-default-text="Name"
                    placeholder="Enter a file name"
                    help-tooltip-default-text="Choose the name for the exported package.">
                  </as-string-input>
                  <as-dropdown
                    label-default-text="Model format"
                    help-tooltip-default-text="Choose the file format for local saving.">
                  </as-dropdown>
                  <div class="export-settings-ok-row">
                    <as-button default-text="OK" no-default-icon></as-button>
                  </div>
                </div>
              </as-hovercard>
            </as-hovercard-wrapper>
          </div>
          <as-button
            default-text="Send to other apps…"
            name="transfer-mesh"
            no-default-icon
            tooltip-default-text="Send the selected parts to another app in Additive App Suite for the next step."
            tooltip-placement="top">
          </as-button>
        </div>
      </as-param-group>
    `);

    const group             = frag.querySelector('as-param-group') as HTMLElement;
    const row               = group.querySelector('div') as HTMLElement;
    this.downloadButton     = group.querySelector('as-button[name="export-all"]') as HTMLElement;
    this.downloadStatus     = group.querySelector('.download-status') as HTMLElement;
    this.#filenameInputEl   = group.querySelector('as-string-input') as HTMLElement;
    this.#formatDropdownEl  = group.querySelector('as-dropdown') as HTMLElement;
    this.#exportSettingsButton  = group.querySelector('#export-settings-btn') as HTMLElement;
    this.transferButton     = group.querySelector('as-button[name="transfer-mesh"]') as HTMLElement;
    const cogBtn            = group.querySelector('as-icon-button') as HTMLElement;
    const hovercard         = group.querySelector('as-hovercard') as HTMLElement;
    const hovercardWrapper  = group.querySelector('as-hovercard-wrapper') as HTMLElement;
    const okBtn             = group.querySelector('as-button[default-text="OK"]') as HTMLElement;

    if (this.#config.packageName) {
      this.#filenameInputEl.setAttribute('value', this.#config.packageName);
    }
    if (this.hasAttribute('no-transfer-button')) this.transferButton.style.display = 'none';

    this.downloadButton.addEventListener('as-button-click', () => this._rebuildExporter());
    hovercardWrapper.addEventListener('as-hovercard-hide', () => {
      (cogBtn as any).forceToggleState?.(false);
    });
    this.#filenameInputEl.addEventListener('as-input-change', () => {
      if (!this.#updatingName) this.#nameUserEdited = true;
    });
    okBtn.addEventListener('as-button-click', () => {
      const name = (this.#filenameInputEl as any)?.value ?? '';
      for (const { el, extraAttrs } of this.#gcBtnEntries) {
        if (extraAttrs.includes('package-name')) el.setAttribute('package-name', name);
      }
      if (name) this.#config.packageName = name;
      hovercard.dispatchEvent(new CustomEvent('as-hovercard-close', { bubbles: true, composed: true }));
    });

    this._syncFormatOptions();

    this.#gcBtnEntries = [];
    for (const desc of ExportBuilder.getButtonDescriptors()) {
      const btn = document.createElement(desc.tagName);
      for (const [attr, val] of Object.entries(desc.defaultAttrs ?? {})) btn.setAttribute(attr, val);
      const extraAttrs = desc.extraAttrs ?? [];
      if (extraAttrs.includes('package-name') && this.#config.packageName) btn.setAttribute('package-name', this.#config.packageName);
      btn.addEventListener('as-button-click', () => this._rebuildExporter());
      this.#gcBtnEntries.push({ el: btn, requiredEnv: desc.requiredEnv, extraAttrs });
      row.insertBefore(btn, this.transferButton);
    }

    return group;
  }

  /** Build the Export scope panel: collapsible accordion containing scope checkboxes. */
  private _buildScopePanel(): HTMLElement {
    const frag = AsExportStep._html(/*html*/`
      <as-param-group class="param-group-collapsible">
        <as-accordion header="Export scope">
          <div data-scope-content>
            <p class="export-scope-desc">Choose what to include in your export.</p>
          </div>
        </as-accordion>
      </as-param-group>
    `);

    const panel   = frag.querySelector('as-param-group') as HTMLElement;
    const content = panel.querySelector('[data-scope-content]') as HTMLElement;
    this.#scopeGroupEl = content;

    if (!this.hasAttribute('no-default-scopes')) {
      for (const item of DEFAULT_SCOPES) this._addExportScopeConfig(item, content);
    }
    for (const item of (this.#config.scopeItems ?? [])) {
      if (!this.#scopeMap.has(item.key)) this._addExportScopeConfig(item, content);
    }

    return panel;
  }

  /** Render a scope checkbox (and any declared children) into container and register them in #scopeMap. */
  private _addExportScopeConfig(item: ExportScopeConfig, container: HTMLElement): void {
    const cb = document.createElement('as-checkbox');
    cb.setAttribute('label-default-text', item.defaultLabelText);
    if (item.labelDictKey) cb.setAttribute('label-dict-key', item.labelDictKey);
    cb.setAttribute('name', item.key);
    if (item.checked !== false) cb.setAttribute('checked', '');

    const accessor = new ExportScopeConfigAccessor(item.key, item.defaultLabelText, cb, item.fileCfgs, item.visible !== false);
    accessor._setOnVisibilityChange(() => this._updateSingleScopeState());
    this.#scopeMap.set(item.key, accessor);
    container.appendChild(cb);

    const childKeys: string[] = [];

    for (const child of (item.children ?? [])) {
      const childCb = document.createElement('as-checkbox');
      childCb.setAttribute('label-default-text', child.defaultLabelText);
      if (child.labelDictKey) childCb.setAttribute('label-dict-key', child.labelDictKey);
      childCb.setAttribute('name', child.key);
      childCb.classList.add('export-scope-child');
      if (child.checked !== false) childCb.setAttribute('checked', '');

      const childAccessor = new ExportScopeConfigAccessor(child.key, child.defaultLabelText, childCb, child.fileCfgs);
      this.#scopeMap.set(child.key, childAccessor);
      childKeys.push(child.key);
      container.appendChild(childCb);

      childCb.addEventListener('as-input-change', () => this._rebuildExporter());
    }

    accessor._setChildKeys(childKeys);

    cb.addEventListener('as-input-change', () => {
      const checked = (cb as any).checked;
      for (const childKey of accessor._childKeys) {
        const childEl = this.#scopeMap.get(childKey)?.el;
        if (childEl) {
          if (checked) childEl.removeAttribute('disabled');
          else childEl.setAttribute('disabled', '');
        }
      }
      this._rebuildExporter();
    });
  }

  /** Repopulate the format dropdown with the current saveFormats list (or DEFAULT_FORMATS). */
  private _syncFormatOptions(): void {
    if (!this.#formatDropdownEl) return;
    this.#formatDropdownEl.innerHTML = '';
    for (const fmt of (this.#config.saveFormats ?? DEFAULT_FORMATS)) {
      const opt = document.createElement('as-option');
      opt.setAttribute('value', fmt.value);
      opt.textContent = fmt.label;
      this.#formatDropdownEl.appendChild(opt);
    }
  }

  /** Incrementally apply #config to the already-built DOM (called by setFromObject after first render). */
  private _applyConfig(): void {
    if (this.#config.packageName != null) this.packageName = this.#config.packageName;
    this._syncFormatOptions();
    for (const item of (this.#config.scopeItems ?? [])) {
      if (!this.#scopeMap.has(item.key)) this._addExportScopeConfig(item, this.#scopeGroupEl!);
    }
    this._updateEnv();
  }

  /** Re-populate the ExportBuilder from the current checkbox state and selected format. Called on any scope or format change. */
  private _rebuildExporter(): void {
    const exp = this.exporter;
    if (!exp) return;
    exp.clear();
    const fmt = this.format;
    if (fmt) try { exp.preferredMeshType = fmt as MeshType; } catch { /* non-mesh format */ }

    const childKeySet = new Set<string>();
    for (const [, accessor] of this.#scopeMap) {
      for (const key of accessor._childKeys) childKeySet.add(key);
    }

    for (const [key, accessor] of this.#scopeMap) {
      if (childKeySet.has(key) || !accessor.checked || accessor.fileCfgs.length === 0) continue;

      const extraChildren: FileDataSchema[] = [];
      for (const childKey of accessor._childKeys) {
        const child = this.#scopeMap.get(childKey);
        if (child?.checked && !child.disabled) extraChildren.push(...child.fileCfgs);
      }

      const [first, ...rest] = accessor.fileCfgs;
      const firstWithChildren = extraChildren.length > 0
        ? { ...first, children: [...(first.children ?? []), ...extraChildren] }
        : first;
      exp.addFile(firstWithChildren);
      for (const cfg of rest) exp.addFile(cfg);
    }
  }

  private _computeDefaultName(): string {
    // Collapse whitespace runs to '-' so the derived file/package name is
    // filename-safe (the header title keeps its spaces).
    const safe = (s: string) => s.trim().replace(/\s+/g, '-');
    // Fall back to 'Untitled' rather than an empty/undefined name so the file and
    // GrabCAD package always have a sensible default.
    const baseName = safe(liveAppData.currentProject?.metadata.name ?? '') || 'Untitled';
    const appId = liveAppData.appId ?? '';
    // Format: <name>_(<appId>), e.g. Test_(GC-Test-App) or Untitled_(GC-Test-App).
    return appId ? `${baseName}_(${safe(appId)})` : baseName;
  }

  private _syncDefaultName(): void {
    const projectId = liveAppData.currentProject?.metadata.id ?? null;
    if (projectId !== this.#activeProjectId) {
      this.#activeProjectId = projectId;
      this.#nameUserEdited = false;
    }
    if (this.#nameUserEdited || this.#config.packageName != null) return;
    const name = this._computeDefaultName();
    if (!name || !this.#filenameInputEl) return;
    this.#updatingName = true;
    this.#filenameInputEl.setAttribute('value', name);
    this.#updatingName = false;
  }

  /**
   * Mirror liveAppData.canDownload (license-gated, seeded by as-app from the
   * appList's downloadEnabled flag) onto the Save button: disabled with an
   * explanatory tooltip when downloads are off.
   */
  private _syncDownloadEnabled(): void {
    const btn = this.downloadButton;
    if (!btn) return;
    const enabled = liveAppData.canDownload !== false;
    btn.toggleAttribute('disabled', !enabled);
    btn.setAttribute('tooltip-default-text', enabled ? SAVE_TOOLTIP : DOWNLOAD_DISABLED_TOOLTIP);
    this.#exportSettingsButton?.classList.toggle('hidden', !enabled);
  }

  /** Show or hide env-gated buttons (e.g. GrabCAD) based on the current as-app[env] attribute. */
  private _updateEnv(): void {
    const env = document.querySelector('as-app')?.getAttribute('env') ?? '';
    for (const { el, requiredEnv } of this.#gcBtnEntries) {
      el.style.display = (requiredEnv && env !== requiredEnv) ? 'none' : '';
    }
  }
}

if (!customElements.get('as-export-group')) customElements.define('as-export-group', AsExportStep);

// Deprecated alias tag: <as-export-step> keeps working but warns once per page.
if (!customElements.get('as-export-step')) {
  customElements.define('as-export-step', DeprecatedEl(
    AsExportStep,
    'as-export-step',
    'as-export-group',
    'as-export-step is deprecated and will be removed in 0.17.0 — use as-export-group instead.',
  ));
}
